import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { financialStatementApi } from '../../services/financialStatementApi';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../ui/ConfirmModal';
import type { LineConfig, AccountMapping } from '../../types/financialStatement';

interface Props {
    canEdit: boolean;
    company: string;
}

const LINE_TYPES = ['Assets', 'Liabilities', 'ProfitLoss'];
const COMPANIES = ['dch', 'dbi', 'bin'];

function getCompanyCount(line: LineConfig, company: string): number {
    switch (company) {
        case 'dch': return line.AccountCountDCH ?? 0;
        case 'dbi': return line.AccountCountDBI ?? 0;
        case 'bin': return line.AccountCountBIN ?? 0;
        default: return 0;
    }
}

// Modal component for viewing linked accounts per line
function LineAccountsModal({ lineNr, lineName, company, onClose }: {
    lineNr: number;
    lineName: string;
    company: string;
    onClose: () => void;
}) {
    const [selectedCompany, setSelectedCompany] = useState(company);
    const [mappings, setMappings] = useState<AccountMapping[]>([]);
    const [loading, setLoading] = useState(true);

    const loadMappings = useCallback(async (comp: string) => {
        setLoading(true);
        try {
            const result = await financialStatementApi.getAccountMappings(comp);
            setMappings(result.mappings.filter(
                m => m.LineNrDebit === lineNr || m.LineNrCredit === lineNr
            ));
        } catch (err) {
            console.error('Failed to load mappings:', err);
        } finally {
            setLoading(false);
        }
    }, [lineNr]);

    useEffect(() => { loadMappings(selectedCompany); }, [selectedCompany, loadMappings]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            Linked Accounts — Line {lineNr}
                        </h3>
                        <p className="text-sm text-gray-500">{lineName}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Company switch */}
                <div className="px-5 py-3 border-b bg-gray-50">
                    <div className="flex gap-2">
                        {COMPANIES.map(c => (
                            <button
                                key={c}
                                onClick={() => setSelectedCompany(c)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                    selectedCompany === c
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white text-gray-700 border hover:bg-gray-100'
                                }`}
                            >
                                {c.toUpperCase()}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto px-5 py-3">
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                        </div>
                    ) : mappings.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-8">
                            No accounts linked for {selectedCompany.toUpperCase()}
                        </p>
                    ) : (
                        <table className="min-w-full">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Side</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {mappings.map(m => (
                                    <tr key={m.AccountNr} className="hover:bg-gray-50">
                                        <td className="px-3 py-1.5 text-sm font-mono">{m.AccountNr}</td>
                                        <td className="px-3 py-1.5 text-sm">{m.AccountName}</td>
                                        <td className="px-3 py-1.5 text-sm text-gray-500">
                                            {m.LineNrDebit === lineNr && m.LineNrCredit === lineNr
                                                ? 'Debit + Credit'
                                                : m.LineNrDebit === lineNr ? 'Debit' : 'Credit'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t bg-gray-50 text-right">
                    <span className="text-xs text-gray-500">
                        {mappings.length} account{mappings.length !== 1 ? 's' : ''} linked
                    </span>
                </div>
            </div>
        </div>
    );
}

export function LineConfigTab({ canEdit, company }: Props) {
    const { toast } = useToast();
    const [lines, setLines] = useState<LineConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingNr, setEditingNr] = useState<number | null>(null);
    const [editData, setEditData] = useState<LineConfig | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [newLine, setNewLine] = useState<LineConfig>({
        LineNr: 0, LineNameGerman: '', LineNameEnglish: '', LineType: 'ProfitLoss', SumLines: null,
    });
    const [modalLine, setModalLine] = useState<{ lineNr: number; lineName: string } | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

    const loadLines = useCallback(async () => {
        setLoading(true);
        try {
            const result = await financialStatementApi.getLinesConfig();
            setLines(result.lines);
        } catch (err) {
            console.error('Failed to load lines:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadLines(); }, [loadLines]);

    const handleSave = async () => {
        if (!editData) return;
        try {
            await financialStatementApi.updateLine(editData.LineNr, editData);
            setEditingNr(null);
            await loadLines();
            toast('Line updated', 'success');
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to update line', 'error');
        }
    };

    const handleCreate = async () => {
        try {
            await financialStatementApi.createLine(newLine);
            setIsAdding(false);
            setNewLine({ LineNr: 0, LineNameGerman: '', LineNameEnglish: '', LineType: 'ProfitLoss', SumLines: null });
            await loadLines();
            toast('Line created', 'success');
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to create line', 'error');
        }
    };

    const handleDelete = async (lineNr: number) => {
        setDeleteConfirm(null);
        try {
            await financialStatementApi.deleteLine(lineNr);
            await loadLines();
            toast(`Line ${lineNr} deleted`, 'success');
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Delete failed', 'error');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {canEdit && (
                <div className="flex justify-end">
                    <button
                        onClick={() => setIsAdding(true)}
                        className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
                    >
                        <Plus className="h-4 w-4" /> Add Line
                    </button>
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="min-w-full">
                    <thead>
                        <tr className="border-b-2 border-gray-200">
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">LineNr</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name (German)</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name (English)</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Type</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-24">Accounts</th>
                            {canEdit && <th className="px-2 py-2 w-20"></th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {/* Add row */}
                        {isAdding && (
                            <tr className="bg-blue-50">
                                <td className="px-3 py-1.5">
                                    <input type="number" value={newLine.LineNr || ''} onChange={e => setNewLine({ ...newLine, LineNr: parseInt(e.target.value) || 0 })} className="w-full rounded border px-2 py-1 text-sm" placeholder="Nr" />
                                </td>
                                <td className="px-3 py-1.5">
                                    <input value={newLine.LineNameGerman} onChange={e => setNewLine({ ...newLine, LineNameGerman: e.target.value })} className="w-full rounded border px-2 py-1 text-sm" placeholder="German name" />
                                </td>
                                <td className="px-3 py-1.5">
                                    <input value={newLine.LineNameEnglish} onChange={e => setNewLine({ ...newLine, LineNameEnglish: e.target.value })} className="w-full rounded border px-2 py-1 text-sm" placeholder="English name" />
                                </td>
                                <td className="px-3 py-1.5">
                                    <select value={newLine.LineType} onChange={e => setNewLine({ ...newLine, LineType: e.target.value })} className="w-full rounded border px-2 py-1 text-sm">
                                        {LINE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </td>
                                <td className="px-3 py-1.5"></td>
                                <td className="px-2 py-1.5 flex gap-1">
                                    <button onClick={handleCreate} className="text-green-600 hover:text-green-800 p-1"><Check className="h-4 w-4" /></button>
                                    <button onClick={() => setIsAdding(false)} className="text-gray-400 hover:text-gray-600 p-1"><X className="h-4 w-4" /></button>
                                </td>
                            </tr>
                        )}

                        {lines.map(line => {
                            const companyCount = getCompanyCount(line, company);
                            const totalCount = line.AccountCountTotal ?? 0;
                            const hasAccounts = totalCount > 0;

                            return (
                                <tr
                                    key={line.LineNr}
                                    className={`${editingNr === line.LineNr ? 'bg-yellow-50' : 'hover:bg-gray-50'} ${hasAccounts && editingNr !== line.LineNr ? 'cursor-pointer' : ''}`}
                                    onClick={() => {
                                        if (hasAccounts && editingNr !== line.LineNr) {
                                            setModalLine({ lineNr: line.LineNr, lineName: line.LineNameEnglish || line.LineNameGerman });
                                        }
                                    }}
                                >
                                    <td className="px-3 py-1.5 text-sm font-mono">{line.LineNr}</td>
                                    <td className="px-3 py-1.5 text-sm">
                                        {editingNr === line.LineNr ? (
                                            <input value={editData!.LineNameGerman} onChange={e => setEditData({ ...editData!, LineNameGerman: e.target.value })} onClick={e => e.stopPropagation()} className="w-full rounded border px-2 py-1 text-sm" />
                                        ) : line.LineNameGerman}
                                    </td>
                                    <td className="px-3 py-1.5 text-sm">
                                        {editingNr === line.LineNr ? (
                                            <input value={editData!.LineNameEnglish} onChange={e => setEditData({ ...editData!, LineNameEnglish: e.target.value })} onClick={e => e.stopPropagation()} className="w-full rounded border px-2 py-1 text-sm" />
                                        ) : line.LineNameEnglish}
                                    </td>
                                    <td className="px-3 py-1.5 text-sm">
                                        {editingNr === line.LineNr ? (
                                            <select value={editData!.LineType} onChange={e => setEditData({ ...editData!, LineType: e.target.value })} onClick={e => e.stopPropagation()} className="w-full rounded border px-2 py-1 text-sm">
                                                {LINE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        ) : (
                                            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
                                                line.LineType === 'Assets' ? 'bg-blue-100 text-blue-700' :
                                                line.LineType === 'Liabilities' ? 'bg-orange-100 text-orange-700' :
                                                'bg-green-100 text-green-700'
                                            }`}>{line.LineType}</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-1.5 text-sm text-right">
                                        {hasAccounts ? (
                                            <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                                                <span className="font-semibold">{companyCount}</span>
                                                <span className="text-gray-400">/</span>
                                                <span className="text-gray-500">{totalCount}</span>
                                            </span>
                                        ) : (
                                            <span className="text-gray-300">0</span>
                                        )}
                                    </td>
                                    {canEdit && (
                                        <td className="px-2 py-1.5" onClick={e => e.stopPropagation()}>
                                            {editingNr === line.LineNr ? (
                                                <div className="flex gap-1">
                                                    <button onClick={handleSave} className="text-green-600 hover:text-green-800 p-1"><Check className="h-4 w-4" /></button>
                                                    <button onClick={() => setEditingNr(null)} className="text-gray-400 hover:text-gray-600 p-1"><X className="h-4 w-4" /></button>
                                                </div>
                                            ) : (
                                                <div className="flex gap-1">
                                                    <button onClick={() => { setEditingNr(line.LineNr); setEditData({ ...line }); }} className="text-gray-400 hover:text-blue-600 p-1"><Pencil className="h-3.5 w-3.5" /></button>
                                                    <button
                                                        onClick={() => setDeleteConfirm(line.LineNr)}
                                                        disabled={hasAccounts}
                                                        className={`p-1 ${hasAccounts ? 'text-gray-200 cursor-not-allowed' : 'text-gray-400 hover:text-red-600'}`}
                                                        title={hasAccounts ? `Cannot delete — ${totalCount} account(s) linked` : 'Delete line'}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Account detail modal */}
            {modalLine && (
                <LineAccountsModal
                    lineNr={modalLine.lineNr}
                    lineName={modalLine.lineName}
                    company={company}
                    onClose={() => setModalLine(null)}
                />
            )}

            {/* Delete confirmation modal */}
            {deleteConfirm !== null && (
                <ConfirmModal
                    title="Delete Line"
                    message={`Delete line ${deleteConfirm}? This cannot be undone.`}
                    confirmLabel="Delete"
                    onConfirm={() => handleDelete(deleteConfirm)}
                    onCancel={() => setDeleteConfirm(null)}
                />
            )}
        </div>
    );
}
